import { Component, OnInit, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import {
  CaisseParametrageRow,
  CaisseParametrePayload,
  CaisseTableRow,
  LogicielCaisseCaisseService,
} from 'app/services/logiciel-caisse/logiciel-caisse-caisse.service';
import {
  LogicielCaisseEntrepotOption,
  LogicielCaisseEntrepotService,
} from 'app/services/logiciel-caisse/logiciel-caisse-entrepot.service';

export interface CaisseParamSection {
  key: string;
  title: string;
  minId: number;
  maxId: number;
}

@Component({
  selector: 'app-logiciel-caisse-caisses',
  templateUrl: './logiciel-caisse-caisses.component.html',
  styleUrls: ['./logiciel-caisse-caisses.component.scss'],
})
export class LogicielCaisseCaissesComponent implements OnInit {
  rows: CaisseTableRow[] = [];
  filteredRows: CaisseTableRow[] = [];
  searchTerm = '';
  loading = false;
  saving = false;
  editId: string | null = null;
  form: UntypedFormGroup;
  entrepotOptions: LogicielCaisseEntrepotOption[] = [];
  /** Aperçu du prochain numéro (création uniquement). */
  nextNumeroPreview: number | null = null;

  paramRow: CaisseTableRow | null = null;
  paramItems: CaisseParametrageRow[] = [];
  paramSearchTerm = '';
  paramLoading = false;
  paramSaving = false;
  showParamPanel = false;

  /** Paramètres retirés de l’interface (non configurables). */
  private readonly hiddenParametrageIds = new Set([19, 20]);

  /** Sections du catalogue (52 paramètres). */
  readonly paramSections: CaisseParamSection[] = [
    { key: 'cloture', title: 'Clôture & journée', minId: 1, maxId: 10 },
    { key: 'ticket', title: 'Ticket & catalogue', minId: 11, maxId: 20 },
    { key: 'caisse', title: 'Caisse & paiement', minId: 21, maxId: 30 },
    { key: 'stock', title: 'Stock & clients', minId: 31, maxId: 40 },
    { key: 'avance', title: 'Options avancées', minId: 41, maxId: 52 },
  ];

  constructor(
    private readonly caisseService: LogicielCaisseCaisseService,
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly modalService: NgbModal,
    private readonly fb: UntypedFormBuilder,
    private readonly toastr: ToastrService
  ) {
    this.form = this.fb.group({
      entrepot_id: [null, Validators.required],
      numero_de_caisse: [null],
    });
    this.form.get('entrepot_id')?.valueChanges.subscribe(() => {
      if (!this.editId) {
        this.updateNextNumeroPreview();
      }
    });
  }

  ngOnInit(): void {
    this.loadRows();
  }

  private loadEntrepotOptions(): void {
    this.entrepotService.getEntrepotOptions().subscribe({
      next: (options) => {
        this.entrepotOptions = options;
      },
      error: (err: Error) => this.toastr.error(err.message),
    });
  }

  loadRows(): void {
    this.loading = true;
    forkJoin({
          options: this.entrepotService.getEntrepotOptions(),
          items: this.caisseService.getCaissesTableRows(),
        }).subscribe({
        next: ({ options, items }) => {
          this.entrepotOptions = options;
          this.rows = items;
          this.applySearch();
          this.loading = false;
          this.refreshFeatherIcons();
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
          this.loading = false;
        },
      });
  }

  statutBadgeClass(statut: string): string {
    const s = (statut || '').toLowerCase();
    if (s.includes('active') || s.includes('actif')) {
      return 'badge-light-success';
    }
    if (s.includes('archive')) {
      return 'badge-light-secondary';
    }
    return 'badge-light-warning';
  }

  isArchived(row: CaisseTableRow): boolean {
    return row._caisseStatutRaw === 'archive';
  }

  applySearch(): void {
    const kw = this.searchTerm.trim().toLowerCase();
    if (!kw) {
      this.filteredRows = [...this.rows];
    } else {
      this.filteredRows = this.rows.filter((row) => {
        const blob = `${row.CAISSE} ${row.UUID} ${row.ENTREPOT} ${row.VENDEUR} ${row.STATUT}`.toLowerCase();
        return blob.includes(kw);
      });
    }
    this.refreshFeatherIcons();
  }

  openCreate(modal: TemplateRef<unknown>): void {
    this.editId = null;
    this.nextNumeroPreview = null;
    this.form.reset();
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  openEdit(row: CaisseTableRow, modal: TemplateRef<unknown>): void {
    if (this.isArchived(row)) {
      return;
    }
    this.editId = row.id;
    this.nextNumeroPreview = null;
    this.form.patchValue({
      entrepot_id: row.entrepot_id != null ? String(row.entrepot_id) : null,
      numero_de_caisse: row.numero_de_caisse ?? null,
    });
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  openParametres(row: CaisseTableRow): void {
    if (this.isArchived(row)) {
      return;
    }
    this.paramRow = row;
    this.paramItems = [];
    this.paramSearchTerm = '';
    this.paramLoading = true;
    this.showParamPanel = true;
    this.caisseService.getCaisseParametres(row.id).subscribe({
      next: (items) => {
        this.paramItems = items.map((item) => ({ ...item }));
        this.paramLoading = false;
      },
      error: (err: Error) => {
        this.paramLoading = false;
        this.toastr.error(err.message);
      },
    });
  }

  closeParamPanel(): void {
    if (this.paramSaving) {
      return;
    }
    this.showParamPanel = false;
    this.paramRow = null;
    this.paramItems = [];
    this.paramSearchTerm = '';
  }

  onParamPanelClick(event: Event): void {
    event.stopPropagation();
  }

  get filteredParamItems(): CaisseParametrageRow[] {
    const visible = this.paramItems.filter(
      (item) => !this.hiddenParametrageIds.has(item.parametrage_id)
    );
    const kw = this.paramSearchTerm.trim().toLowerCase();
    if (!kw) {
      return visible;
    }
    return visible.filter((item) => item.libelle.toLowerCase().includes(kw));
  }

  get activeParamCount(): number {
    return this.filteredParamItems.filter((p) => p.is_active).length;
  }

  paramSectionItems(section: CaisseParamSection): CaisseParametrageRow[] {
    return this.filteredParamItems.filter(
      (item) => item.parametrage_id >= section.minId && item.parametrage_id <= section.maxId
    );
  }

  hasVisibleParamSections(): boolean {
    return this.paramSections.some((section) => this.paramSectionItems(section).length > 0);
  }

  setAllParams(active: boolean): void {
    const visibleIds = new Set(this.filteredParamItems.map((p) => p.parametrage_id));
    for (const item of this.paramItems) {
      if (visibleIds.has(item.parametrage_id)) {
        item.is_active = active;
      }
    }
  }

  setSectionParams(section: CaisseParamSection, active: boolean): void {
    const ids = new Set(this.paramSectionItems(section).map((p) => p.parametrage_id));
    for (const item of this.paramItems) {
      if (ids.has(item.parametrage_id)) {
        item.is_active = active;
      }
    }
  }

  sectionActiveCount(section: CaisseParamSection): number {
    return this.paramSectionItems(section).filter((p) => p.is_active).length;
  }

  save(modalRef: { close: () => void }): void {
    if (this.form.invalid) {
      return;
    }
    const entrepotId = Number(this.form.value.entrepot_id);
    if (!Number.isFinite(entrepotId) || entrepotId <= 0) {
      this.toastr.error('Entrepôt requis.');
      return;
    }
    const body: { entrepot_id: number; numero_de_caisse?: number } = {
      entrepot_id: entrepotId,
    };
    if (this.editId) {
      const rawNum = this.form.value.numero_de_caisse;
      if (rawNum != null && rawNum !== '') {
        const n = Number(rawNum);
        if (Number.isFinite(n) && n > 0) {
          body.numero_de_caisse = n;
        }
      }
    }

    this.saving = true;
    const req = this.editId
      ? this.caisseService.updateCaisse(this.editId, body)
      : this.caisseService.createCaisse(body);

    req.subscribe({
      next: () => {
        this.saving = false;
        modalRef.close();
        this.toastr.success(this.editId ? 'Caisse mise à jour.' : 'Caisse créée.');
        this.loadRows();
      },
      error: (err: Error) => {
        this.saving = false;
        this.toastr.error(err.message);
      },
    });
  }

  saveParametres(): void {
    if (!this.paramRow) {
      return;
    }
    const payload: CaisseParametrePayload[] = this.paramItems.map((item) => ({
      parametrage_id: item.parametrage_id,
      is_active: !!item.is_active,
    }));
    this.paramSaving = true;
    this.caisseService.updateCaisseParametres(this.paramRow.id, payload).subscribe({
      next: () => {
        this.paramSaving = false;
        this.closeParamPanel();
        this.toastr.success('Paramètres enregistrés.');
      },
      error: (err: Error) => {
        this.paramSaving = false;
        this.toastr.error(err.message);
      },
    });
  }

  archiveRow(row: CaisseTableRow): void {
    if (this.isArchived(row)) {
      return;
    }
    if (!confirm(`Archiver ${row.CAISSE} ?`)) {
      return;
    }
    this.caisseService.archiveCaisse(row.id).subscribe({
      next: () => {
        this.toastr.success('Caisse archivée.');
        this.loadRows();
      },
      error: (err: Error) => this.toastr.error(err.message),
    });
  }

  private updateNextNumeroPreview(): void {
    const entrepotId = Number(this.form.value.entrepot_id);
    if (!Number.isFinite(entrepotId) || entrepotId <= 0) {
      this.nextNumeroPreview = null;
      return;
    }
    this.nextNumeroPreview = LogicielCaisseCaisseService.suggestNextNumero(entrepotId, this.rows);
  }

  private refreshFeatherIcons(): void {
    setTimeout(() => {
      if (typeof (window as unknown as { feather?: { replace: () => void } }).feather?.replace === 'function') {
        (window as unknown as { feather: { replace: () => void } }).feather.replace();
      }
    }, 0);
  }
}
